import { createSelector } from 'reselect';
import { has } from 'lodash-es';
import { flatten, clone, uniqBy, pick, isNil, isEmpty } from 'ramda';
import { BasketRateTypes } from 'interfaces';
import { ProductTypes } from 'config/enums';
import { filterByObjectProperties, formatPrice } from 'utils';
import { BookingBuilderRequest } from 'services/BackendApi/types';
import { isInternalUser as isSRSelector } from 'store/modules/auth/selectors';
import { BookingBuilderDomain } from './model';
import { getCancellationPoliciesFromResponse } from './utils';
import { buildAddedAncillaryTransfers, IAddedAncillaryTransfer } from './addedAncillaryTransfers';
import { buildAddedAncillaryGroundServices, IAddedAncillaryGroundService } from './addedAncillaryGroundServices';
import { buildAddedAncillaryExperiences, IAddedAncillaryExperience } from './addedAncillaryExperiences';

export {
  ANCILLARY_ITEM_GENERIC_IDENTIFIER,
  isAncillaryProduct,
  getAncillaryRequestIndex,
} from './addedAncillaryShared';

export const bookingBuilderDomain = (state: any): BookingBuilderDomain => state.bookingBuilder;

export const bookingSelector = createSelector(bookingBuilderDomain, domain => domain);

export const bookingBuilderSelector = createSelector(bookingBuilderDomain, domain => {
  return domain.currentBookingBuilder;
});

export const updateBookingRequestStatusSelector = createSelector(bookingBuilderDomain, domain => {
  return domain.updateBookingRequestStatus;
});

export const bookingRequestSelector = createSelector(bookingBuilderSelector, (booking):
  | BookingBuilderRequest
  | undefined => {
  return booking ? booking.request : undefined;
});

export const bookingResponseSelector = createSelector(bookingBuilderSelector, booking => {
  return booking ? booking.response : undefined;
});

export const bookingResponseCurrencyCodeSelector = createSelector(bookingResponseSelector, bookingResponse => {
  return bookingResponse ? bookingResponse.currency : undefined;
});

export const bookingAvailableProductsSelector = createSelector(bookingResponseSelector, response =>
  response ? response.availableProductSets : undefined
);

export const bookingAvailableAccommodationsSelector = createSelector(
  bookingAvailableProductsSelector,
  availableProductSets => {
    return availableProductSets ? availableProductSets[ProductTypes.ACCOMMODATION] : [];
  }
);

export const bookingAvailableTransfersSelector = createSelector(
  bookingAvailableProductsSelector,
  availableProductSets => {
    return availableProductSets ? availableProductSets[ProductTypes.TRANSFER] : [];
  }
);

export const bookingAvailableGroundServicesSelector = createSelector(
  bookingAvailableProductsSelector,
  availableProducts => {
    return availableProducts ? availableProducts[ProductTypes.GROUND_SERVICE] : [];
  }
);

export const bookingAvailableAddonsSelector = createSelector(bookingAvailableProductsSelector, availableProducts => {
  if (!availableProducts) {
    return [];
  }
  return flatten([availableProducts[ProductTypes.SUPPLEMENT], availableProducts[ProductTypes.FINE]]);
});

export const bookingAvailableSupplementsSelector = createSelector(
  bookingAvailableProductsSelector,
  availableProducts => {
    if (!availableProducts) {
      return [];
    }
    return availableProducts[ProductTypes.SUPPLEMENT];
  }
);

export const bookingAvailableFinesSelector = createSelector(bookingAvailableProductsSelector, availableProducts => {
  if (!availableProducts) {
    return [];
  }
  return availableProducts[ProductTypes.FINE];
});

export const bookingRequestedAccommodationsSelector = createSelector(bookingRequestSelector, request => {
  return request && request[ProductTypes.ACCOMMODATION] ? request[ProductTypes.ACCOMMODATION] : [];
});

export const bookingRequestedTransfersSelector = createSelector(bookingRequestSelector, request => {
  return request && request[ProductTypes.TRANSFER] ? request[ProductTypes.TRANSFER] : [];
});

export const bookingRequestedGroundServicesSelector = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.request) {
    return [];
  }

  return booking.request && booking.request[ProductTypes.GROUND_SERVICE]
    ? booking.request[ProductTypes.GROUND_SERVICE]
    : [];
});

export const bookingRequestedSupplementsSelector = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.request) {
    return [];
  }
  return booking.request && booking.request[ProductTypes.SUPPLEMENT] ? booking.request[ProductTypes.SUPPLEMENT] : [];
});

export const bookingRequestedFinesSelector = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.request) {
    return [];
  }
  return booking.request && booking.request[ProductTypes.FINE] ? booking.request[ProductTypes.FINE] : [];
});

// TODO
// look at getBookingReady in `src/store/modules/bookings/selectors.js`
// this selector should care about SR state and travel agent
export const bookingCanBookSelector = createSelector(bookingBuilderSelector, booking => {
  if (!booking) {
    return false;
  }

  return !booking.response.mustStop && booking.response.canBeBooked;
});

/**
 * HACKS AHEAD
 * this selector SHOULD be able to rely on
 * bookingAvailableTransfersSelector
 * bookingRequestedTransfersSelector
 *
 * instead of the main booking selector
 *
 * however, when we did that, bookingRequestedTransfersSelector was returning STALE data
 * we don't know why
 * that should be investigated
 */
export const bookingRequestedTransfersBreakdownSelector = createSelector(bookingBuilderSelector, booking => {
  if (!booking) {
    return [];
  }

  const selectedTransfers =
    booking && booking.request && booking.request[ProductTypes.TRANSFER]
      ? clone(booking.request[ProductTypes.TRANSFER])
      : [];
  const availableTransfers = clone(booking.response.availableProductSets.Transfer);

  const tempAvailableProducts = flatten(
    availableTransfers.map(transfer => {
      return (transfer.products = transfer.products.map(product => {
        return {
          ...product,
          direction: transfer.meta && transfer.meta.direction ? transfer.meta.direction : undefined,
          nameWithDirection: `${product.name} (${
            transfer.meta && transfer.meta.direction ? transfer.meta.direction : 'Return'
          })`,
        };
      }));
    })
  );

  const selectedTransferProducts = filterByObjectProperties(tempAvailableProducts, selectedTransfers, [
    'uuid',
    'direction',
  ]);

  if (selectedTransferProducts.length >= 1) {
    return selectedTransferProducts.map(stp => stp.nameWithDirection).join(' & ');
  }

  return 'None selected';
});

export const bookingResponseAllErrors = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.response.errors) {
    return [];
  }
  return booking.response.errors;
});

export const bookingResponseAllBookingErrors = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.bookingErrors) {
    return [];
  }
  return booking?.bookingErrors?.map(error => ({
    message: error.detail,
    type: error.type || 'error',
  }));
});

export const bookingResponseNonAccommodationErrors = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.response.errors) {
    return [];
  }
  // following the logic in `getBookingNonAccommodationErrors` in `src/store/modules/bookings/selectors.js`
  // @ts-ignore legacy
  return booking.response.errors.filter(e => e.accommodationProductUuid == null);
});

export const bookingResponseLodgingCountsPerAccommodation = createSelector(bookingBuilderSelector, booking => {
  if (!booking || !booking.response.errors) {
    return [];
  }

  const lodgingCountsPerAccommodation = {};

  booking.request.Accommodation.forEach(reqAccom => {
    // if the dictionary doesn't contain an entry for this UUID, add one
    if (!lodgingCountsPerAccommodation[reqAccom.uuid]) {
      lodgingCountsPerAccommodation[reqAccom.uuid] = 0;
    }

    // now increment its count by 1
    lodgingCountsPerAccommodation[reqAccom.uuid] += 1;
  });

  return lodgingCountsPerAccommodation;
});

export const bookingPotentialBookingSelector = createSelector(bookingResponseSelector, response =>
  response ? response.potentialBooking : undefined
);

export const bookingTextOffersSelector = createSelector(bookingResponseSelector, response =>
  response ? response.textOnlyOffersPerLodging : undefined
);

export const bookingBasketRateType = createSelector(bookingBuilderDomain, domain => {
  const basketRooms = domain.currentBookingBuilder?.request.Accommodation ?? [];
  if (basketRooms.length === 0) {
    return undefined;
  }

  if (basketRooms.some(accomm => has(accomm, 'liveRate') && !has(accomm, 'subProducts'))) {
    if (basketRooms.some(accom => accom.externalRateId === 'instantbook')) {
      return BasketRateTypes.FREE_SALE;
    } else {
      return BasketRateTypes.LIVE;
    }
  } else {
    return BasketRateTypes.STATIC;
  }
});

export const bookingCancellationPoliciesSelector = createSelector(
  bookingResponseSelector,
  bookingBasketRateType,
  (response, rateType) => {
    return getCancellationPoliciesFromResponse(response, rateType);
  }
);

export const bookingPaymentTermsSelector = createSelector(
  bookingPotentialBookingSelector,
  bookingResponseSelector,
  potentialBooking => {
    if (!potentialBooking) {
      return [];
    }

    // long hand, but gives us TS help
    let allPaymentTerms: string[] = [];
    allPaymentTerms = allPaymentTerms.concat(potentialBooking.Accommodation.map(product => product.paymentTerms));
    allPaymentTerms = allPaymentTerms.concat(potentialBooking.Fine.map(product => product.paymentTerms));
    allPaymentTerms = allPaymentTerms.concat(potentialBooking['Ground Service'].map(product => product.paymentTerms));
    allPaymentTerms = allPaymentTerms.concat((potentialBooking.Bespoke ?? []).map(product => product.paymentTerms));
    allPaymentTerms = allPaymentTerms.concat(potentialBooking.Supplement.map(product => product.paymentTerms));
    allPaymentTerms = allPaymentTerms.concat(potentialBooking.Transfer.map(product => product.paymentTerms));

    return uniqBy(a => a, flatten(allPaymentTerms).filter(Boolean));
  }
);

export const bookingOffersTermsSelector = createSelector(
  bookingPotentialBookingSelector,
  bookingTextOffersSelector,
  (potentialBooking, textOffers) => {
    let allOfferTerms: Object[] = [];

    if (potentialBooking) {
      // we lose typehinting, but things were getting ridiculous
      Object.keys(potentialBooking).forEach(productSetKey => {
        potentialBooking[productSetKey].forEach(productSet => {
          productSet.offers.forEach(productSetOffer => {
            allOfferTerms.push({
              name: productSetOffer.offer.name,
              termsAndConditions: productSetOffer.offer.termsAndConditions,
            });
          });
        });
      });
    }

    if (textOffers) {
      allOfferTerms = [
        ...allOfferTerms,
        ...flatten(textOffers).map(({ offer }) => pick(['name', 'termsAndConditions'], offer)),
      ];
    }

    return uniqBy(a => a, allOfferTerms);
  }
);

export const bookingBuilderHotelUuidSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.hotelUuid
);

export const isTAMarginAppliedSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.isTAMarginApplied
);

export const taMarginTypeSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.taMarginType
);

export const taMarginAmountSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.taMarginAmount
);

export const bookingBuilderTotalSelector = createSelector(bookingResponseSelector, response => {
  if (!response) {
    return 0;
  }

  return formatPrice(response.totals.total);
});

export const bookingBuilderResponseHotelUuidSelector = createSelector(bookingResponseSelector, response => {
  if (!response) {
    return undefined;
  }

  return response.hotel.uuid;
});

export const travelAgentUserUuidSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.travelAgentUserUuid
);

export const guestInfoSelector = createSelector(bookingBuilderDomain, bookingBuilderDomain =>
  pick(['guestTitle', 'guestFirstName', 'guestLastName', 'isRepeatGuest', 'comments'], bookingBuilderDomain)
);

export const commentsSelector = createSelector(bookingBuilderDomain, bookingBuilderDomain =>
  pick(['commentsSelector'], bookingBuilderDomain)
);

export const isPristineSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.isPristine
);

export const latestBookingOperationSelector = createSelector(
  bookingBuilderDomain,
  bookingBuilderDomain => bookingBuilderDomain.latestBookingOperation
);

// ---------------- validation --------------------------
const requireValue = (val: any) => (isNil(val) || isEmpty(val) ? ['Required'] : []);

export const guestInfoValidationSelector = createSelector(guestInfoSelector, guestInfo => ({
  guestFirstName: requireValue(guestInfo.guestFirstName),
  guestLastName: requireValue(guestInfo.guestLastName),
}));

export const travelAgentUserUuidValidationSelector = createSelector(
  travelAgentUserUuidSelector,
  isSRSelector,
  (travelAgentUserUuid, isSR) => ({
    travelAgentUserUuid: isSR ? requireValue(travelAgentUserUuid) : [],
  })
);

export const domainValidationSelector = createSelector(
  guestInfoValidationSelector,
  travelAgentUserUuidValidationSelector,
  (guestInfoValidation, travelAgentUserUuidValidation) => ({
    ...guestInfoValidation,
    ...travelAgentUserUuidValidation,
  })
);

export const bookingRequestedAncillaryItemsSelector = createSelector(bookingRequestSelector, request => {
  return request?.ancillaryItems ?? [];
});

export const bookingAncillaryItemSearchDatesSelector = createSelector(bookingBuilderDomain, domain => {
  return domain.ancillaryItemSearchDates ?? [];
});

export const bookingRequestStartDateSelector = createSelector(bookingRequestSelector, request => {
  return request?.startDate;
});

export const bookingRequestEndDateSelector = createSelector(bookingRequestSelector, request => {
  return request?.endDate;
});

export const bookingRequestGuestAgesSelector = createSelector(bookingRequestSelector, request => {
  return request?.guestAges;
});

export const bookingAddedAncillaryTransfersSelector = createSelector(
  bookingPotentialBookingSelector,
  (potentialBooking): IAddedAncillaryTransfer[] =>
    buildAddedAncillaryTransfers(potentialBooking?.[ProductTypes.TRANSFER] ?? [])
);

export const bookingAddedAncillaryGroundServicesSelector = createSelector(
  bookingPotentialBookingSelector,
  (potentialBooking): IAddedAncillaryGroundService[] =>
    buildAddedAncillaryGroundServices(potentialBooking?.[ProductTypes.GROUND_SERVICE] ?? [])
);

export const bookingAddedAncillaryExperiencesSelector = createSelector(
  bookingPotentialBookingSelector,
  (potentialBooking): IAddedAncillaryExperience[] =>
    buildAddedAncillaryExperiences(potentialBooking?.[ProductTypes.BESPOKE] ?? [])
);
